import { useState, useCallback } from 'react';
import { graphQlClient } from '@config/graphqlClient';
import ActivitiesGraphQL from '@graphql/activity.queries';
import { Activity } from '@models/activity.model';
import { SnackType } from '@models/common.model';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useAppDispatch } from '@redux/hooks';
import { useTranslation } from '@hooks/useTranslation';

interface UseActivityReturn {
  activities: Activity[];
  loading: boolean;
  error: string | null;
  page: number;
  pages: number;
  fetchActivities: (
    taskId: string,
    projectId: string,
    page?: number,
    limit?: number,
    append?: boolean,
  ) => Promise<void>;
  fetchActivitiesWithIncidents: (
    taskId: string,
    projectId: string,
    page?: number,
    limit?: number,
    append?: boolean,
  ) => Promise<void>;
  setActivities: (activities: Activity[]) => void;
  loadMore: (
    taskId: string,
    projectId: string,
    limit?: number,
  ) => Promise<void>;
  loadMoreWithIncidents: (
    taskId: string,
    projectId: string,
    limit?: number,
  ) => Promise<void>;
}

export const useActivity = (): UseActivityReturn => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(0);

  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const fetchActivities = useCallback(
    async (
      taskId: string,
      projectId: string,
      page: number = 1,
      limit: number = 10,
      append: boolean = false,
    ) => {
      if (!taskId || !projectId) {
        setActivities([]);
        setPage(1);
        setPages(0);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data: any = await graphQlClient.request(
          ActivitiesGraphQL.queries.getActivitiesByTaskId,
          {
            taskId,
            projectId,
            page,
            limit,
          },
        );

        if (data.getActivitiesByTaskId?.items) {
          const newActivities = data.getActivitiesByTaskId.items;
          const totalPages = Math.ceil(
            data.getActivitiesByTaskId.total / limit,
          );

          setPages(totalPages);
          setPage(page);

          if (append) {
            setActivities((prev) => [...prev, ...newActivities]);
          } else {
            setActivities(newActivities);
          }
        } else {
          if (!append) {
            setActivities([]);
            setPage(1);
            setPages(0);
          }
        }
      } catch (e: any) {
        const errorMessage =
          e?.response?.errors[0]?.message || localeCommon['requestError'];
        setError(errorMessage);

        if (!append) {
          setActivities([]);
          setPage(1);
          setPages(0);
        }

        dispatch(
          appendActionMessage({
            message: errorMessage,
            type: SnackType.ERROR,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [dispatch, localeCommon],
  );

  const fetchActivitiesWithIncidents = useCallback(
    async (
      taskId: string,
      projectId: string,
      page: number = 1,
      limit: number = 10,
      append: boolean = false,
    ) => {
      if (!taskId || !projectId) {
        setActivities([]);
        setPage(1);
        setPages(0);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data: any = await graphQlClient.request(
          ActivitiesGraphQL.queries.getActivityByTaskIdWithIncidents,
          {
            taskId,
            projectId,
            page,
            limit,
          },
        );

        if (data.getActivityByTaskIdWithIncidents?.items) {
          const newActivities = data.getActivityByTaskIdWithIncidents.items;
          const totalPages = Math.ceil(
            data.getActivityByTaskIdWithIncidents.total / limit,
          );

          setPages(totalPages);
          setPage(page);

          if (append) {
            setActivities((prev) => [...prev, ...newActivities]);
          } else {
            setActivities(newActivities);
          }
        } else {
          if (!append) {
            setActivities([]);
            setPage(1);
            setPages(0);
          }
        }
      } catch (e: any) {
        const errorMessage =
          e?.response?.errors[0]?.message || localeCommon['requestError'];
        setError(errorMessage);

        if (!append) {
          setActivities([]);
          setPage(1);
          setPages(0);
        }

        dispatch(
          appendActionMessage({
            message: errorMessage,
            type: SnackType.ERROR,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [dispatch, localeCommon],
  );

  const loadMore = useCallback(
    async (taskId: string, projectId: string, limit: number = 10) => {
      if (page < pages) {
        await fetchActivities(taskId, projectId, page + 1, limit, true);
      }
    },
    [fetchActivities, page, pages],
  );

  const loadMoreWithIncidents = useCallback(
    async (taskId: string, projectId: string, limit: number = 10) => {
      if (page < pages) {
        await fetchActivitiesWithIncidents(
          taskId,
          projectId,
          page + 1,
          limit,
          true,
        );
      }
    },
    [fetchActivitiesWithIncidents, page, pages],
  );

  return {
    activities,
    loading,
    error,
    page,
    pages,
    setActivities,
    fetchActivities,
    fetchActivitiesWithIncidents,
    loadMore,
    loadMoreWithIncidents,
  };
};
